import { useState, useEffect } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Label,
  Switch,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  useTheme,
} from '@rnd-payplus/alloy-react';
import { useTableDensity } from '@rnd-payplus/alloy-react';
import { Moon, Sun, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { getAuthHeaders, getAuthHeadersJson } from '../utils/auth';
import { usePageShell } from '../contexts/PageShellContext';

const SettingsPage = () => {
  const { theme, setTheme } = useTheme();
  const { density, setDensity } = useTableDensity();

  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    slackNotifications: false,
  });

  useEffect(() => {
    const loadPrefs = async () => {
      try {
        const res = await fetch('/api/users/me/preferences', { headers: getAuthHeaders() });
        if (res.ok) {
          const prefs = await res.json();
          setNotifications({
            emailNotifications: prefs.emailNotifications ?? true,
            slackNotifications: prefs.slackNotifications ?? false,
          });
        }
      } catch {
        // Use defaults
      }
    };
    loadPrefs();
  }, []);

  const saveNotifications = async (updates: Partial<typeof notifications>) => {
    const next = { ...notifications, ...updates };
    setNotifications(next);
    try {
      await fetch('/api/users/me/preferences', {
        method: 'PUT',
        headers: getAuthHeadersJson(),
        body: JSON.stringify(next),
      });
    } catch {
      toast.error('Failed to save notification preferences');
    }
  };

  usePageShell({
    title: 'Settings',
    breadcrumbs: [
      { label: 'Home', href: '/dashboard' },
      { label: 'Settings' },
    ],
  });

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Appearance */}
        <Card>
          <CardHeader>
            <CardTitle>Appearance</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Theme</Label>
                <p className="text-sm text-muted-foreground">Toggle dark mode</p>
              </div>
              <div className="flex items-center gap-2">
                {theme === 'dark' ? <Moon className="size-4" /> : <Sun className="size-4" />}
                <Switch
                  checked={theme === 'dark'}
                  onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Table Density</Label>
                <p className="text-sm text-muted-foreground">Adjust row spacing in tables</p>
              </div>
              <Select
                value={density}
                onValueChange={(value) => setDensity(value as 'comfortable' | 'compact')}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="comfortable">Comfortable</SelectItem>
                  <SelectItem value="compact">Compact</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card>
          <CardHeader>
            <CardTitle>Notifications</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Email Notifications</Label>
                <p className="text-sm text-muted-foreground">Receive notifications via email</p>
              </div>
              <Switch
                checked={notifications.emailNotifications}
                onCheckedChange={(checked) => saveNotifications({ emailNotifications: checked })}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Slack Notifications</Label>
                <p className="text-sm text-muted-foreground">Receive notifications via Slack</p>
              </div>
              <Switch
                checked={notifications.slackNotifications}
                onCheckedChange={(checked) => saveNotifications({ slackNotifications: checked })}
              />
            </div>
          </CardContent>
        </Card>

        {/* Feedback */}
        <Card>
          <CardHeader>
            <CardTitle>Feedback</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Help us improve Connectivity Hub by sharing your feedback.
            </p>
            <Button variant="outline" disabled>
              <MessageSquare className="mr-2 size-4" />
              Send Feedback (Coming Soon)
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SettingsPage;
